import React, { useContext, useMemo, useState } from 'react';
import { FaCheck, FaPen, FaTimes } from 'react-icons/fa';
import { MachineContext } from '../DataContexts/MachineContext';
import { formatProvinceLabel } from './mapFilters';
import './MappingIssues.css';

// DJB-247: "Resolve mapping issues" — the small section under the Accounts ›
// Maps map that lists every machine the map-data endpoint returned without
// usable coordinates (the `offMap` half of mapData.splitMappable) so the
// admin can fix the address right there.
//
// The /machines/map-data row only carries name · city · province, so the
// street / nr / postcode and the full DTO the PUT needs come from the
// MachineContext machine with the same id (`machineId` on the row === `id`
// on the machine).
//
// DJB-Server PR #80: geocoding is a separate controller now, the machine
// PUT does NOT re-geocode. Saving therefore runs in two steps:
//   1. address edited → MachineContext.updateMachine (PUT /machines/{id},
//      same body + housenumber / postalcode normalisation as the
//      MachineDetails.js info edit);
//   2. manual coordinates filled in → updateMachineGeodata
//      (PUT /machines/{id}/geodata), otherwise geocodeMachine
//      (POST /machines/{id}/geodata/geocode) on the stored address — also
//      when nothing was edited, so "Opslaan" doubles as "try again".
// Afterwards the page re-fetches the map data (`onSaved`) — a machine that
// got coordinates leaves this list and drops onto the map.

export const MAPPING_ISSUES_TITLE = 'Resolve mapping issues';
export const MAPPING_ISSUES_SUBTITLE = 'Controleer adresgegevens van deze accounts.';
export const MAPPING_ISSUES_EMPTY = 'Geen problemen: alle machines hebben coördinaten.';

export const ADDRESS_FIELDS = Object.freeze([
  Object.freeze({ key: 'street', label: 'Straat', type: 'text' }),
  Object.freeze({ key: 'housenumber', label: 'Nr', type: 'number' }),
  Object.freeze({ key: 'postalcode', label: 'Postcode', type: 'number' }),
  Object.freeze({ key: 'city', label: 'Plaats', type: 'text' }),
  Object.freeze({ key: 'province', label: 'Provincie', type: 'province' }),
]);

const text = (value) => (value == null || value === '' ? '—' : String(value));

// '' → null, otherwise a number (same rule as MachineDetails.handleSaveInfo)
const toNullableNumber = (value) => (value === '' || value == null ? null : Number(value));

export const COORDS_LABEL = 'Of coördinaten manueel:';
export const COORDS_HINT = 'Leeg laten om het adres opnieuw te geocoderen.';
export const COORDS_ERROR_BOTH = 'Geef zowel breedte- als lengtegraad in.';
export const COORDS_ERROR_RANGE = 'Breedtegraad ligt tussen -90 en 90, lengtegraad tussen -180 en 180.';

export const buildAddressForm = (machine) => ({
  street: machine?.street ?? '',
  housenumber: machine?.housenumber ?? '',
  postalcode: machine?.postalcode ?? '',
  city: machine?.city ?? '',
  province: machine?.province ?? '',
  // manual override, never prefilled: the row is here because the machine
  // has no usable coordinates
  latitude: '',
  longitude: '',
});

// The manual coordinates of the form: null when both are left empty (→
// re-geocode), { latitude, longitude } as numbers when both are valid, or
// { error } when only one is given or one is out of range.
export const readManualCoordinates = (form) => {
  const lat = String(form?.latitude ?? '').trim();
  const lng = String(form?.longitude ?? '').trim();
  if (lat === '' && lng === '') return null;
  if (lat === '' || lng === '') return { error: COORDS_ERROR_BOTH };
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180
  ) {
    return { error: COORDS_ERROR_RANGE };
  }
  return { latitude, longitude };
};

// true when the normalised address differs from what the machine has
export const isAddressChanged = (form, machine) => {
  const next = normalizeAddressForm(form);
  const current = normalizeAddressForm(buildAddressForm(machine));
  return Object.keys(next).some((key) => (next[key] ?? null) !== (current[key] ?? null));
};

export const normalizeAddressForm = (form) => ({
  street: form.street,
  housenumber: toNullableNumber(form.housenumber),
  postalcode: toNullableNumber(form.postalcode),
  city: form.city,
  province: form.province === '' ? null : form.province,
});

const MappingIssues = ({ machines: offMap, onSaved }) => {
  const {
    machines: fleet = [],
    updateMachine,
    updateMachineGeodata,
    geocodeMachine,
  } = useContext(MachineContext) || {};
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fleetById = useMemo(() => {
    const map = new Map();
    (fleet || []).forEach((machine) => {
      if (machine && machine.id != null) map.set(String(machine.id), machine);
    });
    return map;
  }, [fleet]);

  // province dropdown: every province in the fleet (same source as the
  // MachineDetails select) + whatever the edited machine currently has
  const provinces = useMemo(() => {
    const set = new Set();
    (fleet || []).forEach((machine) => machine?.province && set.add(machine.province));
    (offMap || []).forEach((row) => row?.province && set.add(row.province));
    if (form && form.province) set.add(form.province);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'nl', { sensitivity: 'base' }));
  }, [fleet, offMap, form]);

  const rows = useMemo(
    () =>
      (offMap || []).map((row) => {
        const full = fleetById.get(String(row.machineId));
        return {
          id: row.machineId,
          name: row.name || full?.name || '',
          street: full?.street,
          housenumber: full?.housenumber,
          postalcode: full?.postalcode,
          city: row.city ?? full?.city,
          province: row.province ?? full?.province,
          full,
        };
      }),
    [offMap, fleetById]
  );

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm(buildAddressForm(row.full));
    setFormError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(null);
    setFormError(null);
  };

  const changeField = (key, value) => {
    setFormError(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async (row) => {
    if (!row.full || !form || saving) return;
    const coordinates = readManualCoordinates(form);
    if (coordinates && coordinates.error) {
      setFormError(coordinates.error);
      return;
    }
    setSaving(true);
    try {
      if (isAddressChanged(form, row.full)) {
        await updateMachine(row.full.id, { ...row.full, ...normalizeAddressForm(form) });
      }
      if (coordinates) {
        await updateMachineGeodata(row.full.id, coordinates);
      } else {
        await geocodeMachine(row.full.id);
      }
      setEditingId(null);
      setForm(null);
      setFormError(null);
      if (onSaved) onSaved();
    } catch (error) {
      // the MachineContext call already surfaced an error notification
      console.error('Failed to fix machine coordinates:', error);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event, row) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      save(row);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  };

  const renderCoordinate = (key, label, row) => (
    <label className="mapping-issues-coord" htmlFor={`mapping-issue-${row.id}-${key}`}>
      <span>{label}</span>
      <input
        id={`mapping-issue-${row.id}-${key}`}
        className={`mapping-issues-input is-${key}`}
        type="number"
        step="any"
        inputMode="decimal"
        value={form[key]}
        disabled={saving}
        onChange={(event) => changeField(key, event.target.value)}
        onKeyDown={(event) => onKeyDown(event, row)}
      />
    </label>
  );

  const renderField = (field, row) => {
    const inputId = `mapping-issue-${row.id}-${field.key}`;
    if (field.type === 'province') {
      return (
        <select
          id={inputId}
          className="mapping-issues-input"
          aria-label={field.label}
          value={form.province}
          disabled={saving}
          onChange={(event) => changeField('province', event.target.value)}
          onKeyDown={(event) => onKeyDown(event, row)}
        >
          <option value="">—</option>
          {provinces.map((province) => (
            <option key={province} value={province}>
              {formatProvinceLabel(province)}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        id={inputId}
        className={`mapping-issues-input is-${field.key}`}
        type={field.type}
        aria-label={field.label}
        value={form[field.key]}
        disabled={saving}
        onChange={(event) => changeField(field.key, event.target.value)}
        onKeyDown={(event) => onKeyDown(event, row)}
      />
    );
  };

  return (
    <section className="mapping-issues" aria-labelledby="mapping-issues-title">
      <header className="mapping-issues-header">
        <div className="mapping-issues-title-row">
          <h2 id="mapping-issues-title" className="mapping-issues-title">
            {MAPPING_ISSUES_TITLE}
          </h2>
          {rows.length > 0 && (
            <span
              className="mapping-issues-count"
              aria-label={`${rows.length} ${rows.length === 1 ? 'machine' : 'machines'} zonder coördinaten`}
            >
              {rows.length}
            </span>
          )}
        </div>
        <p className="mapping-issues-subtitle">{MAPPING_ISSUES_SUBTITLE}</p>
      </header>

      {rows.length === 0 ? (
        <p className="mapping-issues-empty">{MAPPING_ISSUES_EMPTY}</p>
      ) : (
        <div className="mapping-issues-table-wrap">
          <table className="mapping-issues-table">
            <thead>
              <tr>
                <th scope="col">Naam</th>
                {ADDRESS_FIELDS.map((field) => (
                  <th scope="col" key={field.key} className={`is-${field.key}`}>
                    {field.label}
                  </th>
                ))}
                <th scope="col" className="is-actions">
                  <span className="visually-hidden">Acties</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const editing = editingId === row.id && form;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`mapping-issues-row${editing ? ' is-editing' : ''}`}
                      data-machine-id={row.id}
                    >
                      <th scope="row" className="mapping-issues-name">
                        {row.name || 'Naamloze machine'}
                      </th>
                      {ADDRESS_FIELDS.map((field) => (
                        <td key={field.key} className={`is-${field.key}`}>
                          {editing ? (
                            renderField(field, row)
                          ) : field.key === 'province' ? (
                            text(formatProvinceLabel(row.province))
                          ) : (
                            text(row[field.key])
                          )}
                        </td>
                      ))}
                      <td className="is-actions">
                        {editing ? (
                          <div className="mapping-issues-actions">
                            <button
                              type="button"
                              className="mapping-issues-btn is-save"
                              onClick={() => save(row)}
                              disabled={saving}
                              aria-label={`Adres van ${row.name} opslaan`}
                              title="Opslaan"
                            >
                              <FaCheck aria-hidden="true" />
                              <span>{saving ? 'Bezig…' : 'Opslaan'}</span>
                            </button>
                            <button
                              type="button"
                              className="mapping-issues-btn is-cancel"
                              onClick={cancelEdit}
                              disabled={saving}
                              aria-label={`Bewerken van ${row.name} annuleren`}
                              title="Annuleren"
                            >
                              <FaTimes aria-hidden="true" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="mapping-issues-btn is-edit"
                            onClick={() => startEdit(row)}
                            disabled={!row.full || editingId !== null}
                            aria-label={`Adres van ${row.name} bewerken`}
                            title={
                              row.full
                                ? 'Adres bewerken'
                                : 'Machinegegevens nog niet geladen'
                            }
                          >
                            <FaPen aria-hidden="true" />
                            <span>Bewerk</span>
                          </button>
                        )}
                      </td>
                    </tr>
                    {editing && (
                      <tr className="mapping-issues-coords-row is-editing" data-machine-id={row.id}>
                        <td colSpan={ADDRESS_FIELDS.length + 2}>
                          <div className="mapping-issues-coords">
                            <span className="mapping-issues-coords-label">{COORDS_LABEL}</span>
                            {renderCoordinate('latitude', 'Breedtegraad', row)}
                            {renderCoordinate('longitude', 'Lengtegraad', row)}
                            {formError ? (
                              <span className="mapping-issues-error" role="alert">
                                {formError}
                              </span>
                            ) : (
                              <span className="mapping-issues-coords-hint">{COORDS_HINT}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default MappingIssues;
